import { inputAnatomy as parts } from '@chakra-ui/anatomy'
import type {
  PartsStyleFunction,
  PartsStyleObject,
  SystemStyleObject,
} from '@chakra-ui/theme-tools'
import { mode } from '@chakra-ui/theme-tools'

const baseStyle: PartsStyleObject<typeof parts> = {
  field: {
    width: '100%',
    minW: 0,
    maxW: '100%',
    outline: 0,
    position: 'relative',
    appearance: 'none',
    lineHeight: 'input',
    transitionProperty: 'common',
    transitionDuration: 'normal',
  },
}

const size: Record<string, SystemStyleObject> = {
  lg: {
    height: 'auto',
    px: 4,
    py: 3,
    borderRadius: 'md',
    fontSize: 'lg',
  },

  md: {
    // height: 9,
    px: 5,
    py: 4,
    borderRadius: 'sm',
    fontSize: {
      base: 'md',
      lg: 'md',
    },
  },

  sm: {
    height: 'auto',
    px: 3,
    py: 1,
    borderRadius: 'sm',
    fontSize: 'sm',
  },

  xs: {
    height: 'auto',
    px: 2,
    py: 1,
    borderRadius: 'sm',
    fontSize: 'xs',
  },
}

const sizes: Record<string, PartsStyleObject<typeof parts>> = {
  lg: {
    field: size.lg,
    addon: size.lg,
  },
  md: {
    field: size.md,
    addon: size.md,
  },
  sm: {
    field: size.sm,
    addon: size.sm,
  },
  xs: {
    field: size.xs,
    addon: size.xs,
  },
}

const variantOutline: PartsStyleFunction<typeof parts> = (props) => {
  return {
    field: {
      border: 0,
      bg: mode('neutral.50', 'neutral.900')(props),
      color: mode('primary.800', 'primary.200')(props),
      _hover: {
        bg: 'neutral.100',
        _disabled: {
          bg: 'neutral.50',
        }
      },
      _readOnly: {
        boxShadow: 'none !important',
        userSelect: 'all',
      },
      _disabled: {
        bg: mode('neutral.50', 'neutral.900')(props),
        color: mode('primary.800', 'neutral.300')(props),
        cursor: 'not-allowed',
        opacity: 1,
      },
      _invalid: {},
      _focus: {
        bg: 'neutral.100',
        zIndex: 1,
      },
    },
    addon: {
      border: 'none',
      bg: mode('neutral.50', 'whiteAlpha.300')(props),
      color: 'neutral.300',
    },
  }
}

const variantFilled: PartsStyleFunction<typeof parts> = (props) => {
  return {
    field: {
      border: '2px solid',
      borderColor: 'transparent',
      bg: mode('neutral.50', 'whiteAlpha.50')(props),
      _hover: {
        bg: mode('neutral.200', 'whiteAlpha.100')(props),
      },
      _readOnly: {
        boxShadow: 'none !important',
        userSelect: 'all',
      },
      _disabled: {
        opacity: 0.4,
        cursor: 'not-allowed',
      },
      _invalid: {},
      _focus: {
        bg: 'transparent',
      },
    },
    addon: {
      border: '0 solid',
      borderColor: 'transparent',
      bg: mode('neutral.100', 'whiteAlpha.50')(props),
    },
  }
}

const variantFlushed: PartsStyleFunction<typeof parts> = (props) => {
  return {
    field: {
      bgColor: 'input-bg',
      borderBottom: '2px solid',
      borderColor: 'inherit',
      borderBottomColor: 'input-border',
      borderRadius: 0,
      boxShadow: 'none !important',
      color: 'input-color',
      px: 4,
      fontWeight: 'bold',
      _placeholder: {
        color: 'gray.400',
      },
      _readOnly: {
        boxShadow: 'none !important',
        userSelect: 'all',
      },
      _invalid: {
        bgColor: 'red.50',
        borderBottomColor: 'red.500',
        _placeholder: {
          color: 'red.500',
        },
      },
      _disabled: {
        borderBottomColor: 'neutral.200',
        color: 'inherit',
        opacity: 1,
      },
      _focus: {
        borderBottomColor: 'input-focused-border',
        _invalid: {
          boxShadow: 'none !important',
          bgColor: 'red.50',
          borderBottomColor: 'red.500',
        },
      },
    },
    addon: {
      borderBottom: '0 solid',
      borderColor: 'inherit',
      borderRadius: 0,
      px: 0,
      bg: 'transparent',
    },
  }
}

const variantUnstyled: PartsStyleObject<typeof parts> = {
  field: {
    bg: 'transparent',
    px: 0,
    height: 'auto',
  },
  addon: {
    bg: 'transparent',
    px: 0,
    height: 'auto',
  },
}

const variants = {
  outline: variantOutline,
  filled: variantFilled,
  flushed: variantFlushed,
  unstyled: variantUnstyled,
}

const defaultProps = {
  size: 'md',
  variant: 'flushed',
}

export const Input = {
  parts: parts.keys,
  baseStyle,
  sizes,
  variants,
  defaultProps,
}
